import { findStoryElement } from 'utils/blocks/story-element-reference';
import { forEach, isObject, get, last, forOwn, has } from 'lodash';
import type { CmsModel } from 'types/cms';
import type {
	BBInheritanceValue,
	BBStates,
	SelectedBBSingle,
	StoryMediaPlatform,
	CardData,
	CardEditorType,
	StoryMediaQuery,
	StorySymbols,
	BBSymbolInfo,
	BBModel,
	BBSymbolInstance,
} from 'types/story';
import { getCustomCssProp } from 'utils/blocks/get-custom-css-prop';
import { OTHER_PROPS, UNIFORM_PROPS, CSS_PROPS } from 'common/constants/component-props';
import { isSymbol } from 'utils/blocks/symbol';
import { IMAGE_PARAMS, VIDEO_PARAMS, getSourceType } from 'utils/assets';
import cms from 'utils/cms';
import { orderMediaQueryConfig } from 'utils/story-media-query';
import { getSelectedElementModel } from 'admin/utils/get-selected-element-model';
import { propFieldsConfig, text } from 'admin/components/pages/Story/CardEditor/Inspector/config/prop-fields-config';
import type { EditableProps } from 'admin/components/pages/Story/CardEditor/Inspector/config/editable-props';
import type { FieldConfig, InspectorTypes } from 'admin/components/pages/Story/CardEditor/Inspector/types';

import { getInheritanceInfo } from './get-inheritance-info';

// todo: refactor this file to split it into smaller files

// Extract the property name from the full path (e.g., 'uiConfig.styles.color' -> 'color')
const getPropertyFromName = (name: string) => name.slice(name.lastIndexOf('.') + 1);
/**
 * Get field properties for a certain editable property of the BuildingBlock
 * @param prop editable property name
 * @return {} field properties
 */
const getPropConfig: InspectorTypes.GetPropConfig = prop => propFieldsConfig[prop] || text;

const getEditableConfig = ({
	editableProps,
	elementPath,
	elementInheritance,
	state,
	currentMediaQuery: cmq,
}: {
	editableProps: EditableProps;
	elementPath: SelectedBBSingle['path'];
	elementInheritance: SelectedBBSingle['inheritance'];
	state: BBStates;
	currentMediaQuery: StoryMediaPlatform;
}) => {
	const editableConfig: FieldConfig[] = [];

	forOwn(editableProps, (props, propsKey) => {
		forEach(props, (propValue, propKey) => {
			if (isObject(propValue) && propValue) {
				// In general, it's a styles object
				const { name: propName, keys } = propValue;

				forEach(keys, key => {
					const propertyInheritance: BBInheritanceValue[] = get(
						elementInheritance,
						`${propsKey}.${propName}.${key}`
					)!;
					const inheritance = last(propertyInheritance);

					editableConfig.push({
						name: key,
						path: `elements.${elementPath}.uiConfig.${propsKey}.${propName}.${state}.${cmq}.${key}`,
						inheritance,
						valuePath: `uiConfig.${propsKey}.${propName}.${key}`,
					});
				});
			} else {
				const propertyInheritance: BBInheritanceValue[] = get(elementInheritance, `${propsKey}.${propValue}`)!;
				const inheritance = last(propertyInheritance);
				const isUniformProp = has(UNIFORM_PROPS, propValue);

				editableConfig.push({
					name: propValue,
					path: `elements.${elementPath}.uiConfig.${propsKey}.${propValue}${
						isUniformProp ? '' : `.${state}.${cmq}`
					}`,
					inheritance,
					valuePath: `uiConfig.${propsKey}.${propValue}`,
				});
			}
		});
	});

	return editableConfig;
};

const {
	backgroundPosition: bgPosition,
	backgroundSize: bgSize,
	backgroundImage: bgImage,
	backgroundRepeat: bgRepeat,
} = CSS_PROPS.background;

const getPropertiesOnMediaChange = (props: {
	type: 'card' | 'image' | 'video' | 'videoPoster' | 'lottie' | 'visualCompare';
	target: { value: string; name: string };
	posterUrl?: string;
	transcodingId?: string;
	element: SelectedBBSingle;
	cardData: CardData;
	symbols?: StorySymbols;
	storyElements: CardEditorType['storyElements'];
	mediaQuery: StoryMediaQuery;
	currentMediaQuery: StoryMediaPlatform;
	currentState: BBStates;
}) => {
	const DELETE = undefined;
	const { name, value } = props.target;
	const [propName, ...nameBaseParts] = name.split('.').reverse();
	const nameBase = nameBaseParts.reverse().join('.').replace('.other.', '.styles.');
	const nameBaseOther = nameBase.replace('.styles.', '.other.');
	const source = getSourceType(value);
	const map = new Map<string, string | undefined>();

	switch (props.type) {
		case 'card':
			// TODO: handle case if value is a link to video without file extension
			if (propName === OTHER_PROPS.card.backgroundPoster) {
				map.set(`${nameBaseOther}.${OTHER_PROPS.card.backgroundPoster}`, value);
			} else if (source === 'video' || source === 'hls' || source === 'youtube' || source === 'vimeo') {
				map.set(`${nameBaseOther}.${OTHER_PROPS.card.backgroundVideo}`, value);
				map.set(`${nameBaseOther}.${OTHER_PROPS.card.backgroundPoster}`, props.posterUrl);
				map.set(`${nameBase}.${bgSize}`, DELETE);
				map.set(`${nameBase}.${bgImage}`, value ? 'none' : DELETE);
			} else {
				map.set(`${nameBase}.${bgImage}`, value);

				if (!value || value === 'none') {
					map.set(`${nameBase}.${bgSize}`, DELETE);
					map.set(`${nameBase}.${bgPosition}`, DELETE);
					map.set(`${nameBase}.${bgRepeat}`, DELETE);
				}

				map.set(`${nameBase}.${CSS_PROPS.background.objectFit}`, DELETE);
				map.set(`${nameBase}.${CSS_PROPS.background.objectPosition}`, DELETE);
				map.set(`${nameBaseOther}.${OTHER_PROPS.card.backgroundVideo}`, value ? 'none' : DELETE);
				map.set(`${nameBaseOther}.${OTHER_PROPS.card.backgroundPoster}`, value ? 'none' : DELETE);
			}
			break;
		case 'image':
			/* eslint-disable no-case-declarations */
			let image: string = value;
			let size: string | undefined = DELETE;
			let position: string | undefined = DELETE;

			if (value && value !== 'none') {
				try {
					const url = new URL(value);
					const fsz = url.searchParams.get(IMAGE_PARAMS.fsz);
					const rect = url.searchParams.get(IMAGE_PARAMS.rect);

					/* get rid of `fsz` param at background-image and assign it to background-size */
					if (fsz) {
						size = fsz;
						url.searchParams.delete(IMAGE_PARAMS.fsz);
					}

					/* get rid of `rect` param at background-image and assign it to background-position */
					if (rect) {
						position = rect;
						url.searchParams.delete(IMAGE_PARAMS.rect);
					}

					image = url.href;
				} catch (e) {
					console.error('Failed to parse image URL', e);
				}
			}
			/* eslint-enable no-case-declarations */
			map.set(`${nameBase}.${bgImage}`, image);
			map.set(`${nameBase}.${bgSize}`, size);
			map.set(`${nameBase}.${bgPosition}`, position);
			break;
		case 'visualCompare':
			map.set(`${nameBaseOther}.${OTHER_PROPS.visualCompare[propName]}`, value);
			break;
		case 'video': {
			let src = value;
			if (value && value !== 'none') {
				try {
					const url = new URL(value);
					if (props.transcodingId) url.searchParams.set(VIDEO_PARAMS.transcodingId, props.transcodingId);
					src = url.toString();
				} catch (e) {
					console.error('Failed to parse video URL', e);
				}
			}

			map.set(`${nameBaseOther}.${OTHER_PROPS.video.videoSrc}`, src);
			map.set(`${nameBaseOther}.${OTHER_PROPS.video.videoPoster}`, props.posterUrl);
			break;
		}
		case 'videoPoster':
			map.set(`${nameBaseOther}.${OTHER_PROPS.video.videoPoster}`, value);
			break;
		case 'lottie':
			map.set(`${nameBaseOther}.${OTHER_PROPS.lottie.ltSrc}`, value);
			break;
		default:
			break;
	}

	// reset values on image replace (reset bg position that was suitable for previous img src)
	if (props.type === 'image') {
		const cursor: { platform?: StoryMediaPlatform; image?: string; position?: string } = {};
		const curMQ = props.currentMediaQuery;
		const getSrc = (href: string = '') => href.split('?')[0];
		const { model } = getSelectedElementModel({
			element: props.element,
			cardData: props.cardData,
			symbols: props.symbols,
			storyElements: props.storyElements,
		});
		const modelStyles = model?.uiConfig.componentProps.styles ?? {};

		// order media queries by order of inheritance
		const orderedMediaQueryConfig = orderMediaQueryConfig(props.mediaQuery, {
			defaultFirst: true,
			minOrder: 'asc',
			maxOrder: 'desc',
		});

		orderedMediaQueryConfig.forEach(mq => {
			// get background image per platform
			const src = getSrc(get(modelStyles, [props.currentState, mq.key, bgImage]));
			const position = getSrc(get(modelStyles, [props.currentState, mq.key, bgPosition]));

			// is never been assigned yet
			if (!cursor.platform && src) {
				cursor.platform = mq.key;
				cursor.image = src;
				cursor.position = position;
			}

			// is can inherit value from `cursor`
			if (cursor.platform && cursor.platform !== mq.key && position) {
				if (src) {
					cursor.platform = mq.key;
					cursor.image = src;
				} else {
					// reset background position that was defined for previous image in specific platform
					map.set(`${nameBase.replace(curMQ, mq.key)}.${bgPosition}`, DELETE);
				}
			}
		});
	}

	return { name: [...map.keys()], value: [...map.values()] };
};

const getParentPath = (elementPath: string) =>
	elementPath.split('.').slice(0, -2 /* drop ".children.index" */).join('.');

export const getSiblingPath = (elementPath: string) => {
	const strArr = elementPath.split('.');
	const currentIndex = parseInt(strArr.pop() || '', 10);
	if (Number.isNaN(currentIndex)) {
		return { prev: null, next: null };
	}
	return {
		prev: currentIndex > 0 ? [...strArr, currentIndex - 1].join('.') : null,
		next: [...strArr, currentIndex + 1].join('.'),
	};
};

interface GetSymbolInfoPath {
	storyElements?: BBModel[];
	elementPathname: string; // elements.1.children....
	selectedElement: SelectedBBSingle;
	card: CardData;
	symbols: StorySymbols;
	originalPath?: string;
}

/**
 * Get info about symbol by pathname, in case of provided pathname refers to symbol.
 */
export const getSymbolInfoByPath = ({
	elementPathname,
	selectedElement,
	card,
	symbols,
	originalPath,
	storyElements,
}: GetSymbolInfoPath): { isSymbol: boolean; info?: BBSymbolInfo } => {
	if (findStoryElement(storyElements ?? [], selectedElement._id)) {
		return { isSymbol: false };
	}

	if (elementPathname.replace('elements.', '') === selectedElement.path && !originalPath) {
		return { isSymbol: isSymbol(selectedElement), info: selectedElement.symbol };
	}

	const target: BBModel | undefined = get(card, elementPathname);

	if (target !== undefined) {
		if (!isSymbol(target)) {
			return { isSymbol: false };
		}

		let info: BBSymbolInfo = target.symbol;

		const isElementInsideTarget = originalPath && elementPathname !== originalPath;
		if (isElementInsideTarget) {
			const { masterId, instanceId } = target.symbol;
			const instance = symbols[masterId].instance[instanceId];
			const searchable: BBSymbolInstance = get(instance, originalPath.replace(`${elementPathname}.`, ''));
			info = searchable.symbol;
		}

		return { isSymbol: true, info };
	}

	const parentPath = getParentPath(elementPathname);

	return parentPath === ''
		? { isSymbol: false }
		: getSymbolInfoByPath({
				elementPathname: parentPath,
				selectedElement,
				card,
				symbols,
				originalPath: originalPath || elementPathname,
				storyElements,
			});
};

type GetDefaultValue = {
	propName: 'styles' | 'other';
	key: string;
	computedStyle: CSSStyleDeclaration;
	element: SelectedBBSingle;
	elementNode: HTMLElement;
	contentWindow: Window;
};

const getDefaultValue = ({ propName, key, contentWindow, computedStyle, elementNode, element }: GetDefaultValue) => {
	if (propName !== 'styles') return undefined;

	let defaultValue = getOverridenValue(key, computedStyle[key]);

	const customProp = getCustomCssProp(element.type, key, { uiConfig: element.uiConfig });
	if (customProp) {
		const { selectorPostfix, cssProperty } = customProp;
		const customNode = selectorPostfix ? elementNode.querySelector(selectorPostfix()) : null;

		if (customNode) {
			defaultValue = getOverridenValue(cssProperty, contentWindow.getComputedStyle(customNode)[cssProperty]);
		}
	}

	return defaultValue;

	// Override default values for some properties
	function getOverridenValue(propKey: string, defaultPropValue: any) {
		if (propKey === CSS_PROPS.effects.pointerEvents) {
			return 'auto';
		}
		if (
			(propKey === CSS_PROPS.grid.rowGap ||
				propKey === CSS_PROPS.grid.columnGap ||
				propKey === CSS_PROPS.grid.gap) &&
			Number.isNaN(parseFloat(defaultPropValue))
		) {
			return '0';
		}

		return defaultPropValue;
	}
};

/**
 * Gets the last value from property inheritance and resolves any CMS reference values
 * @param inheritance - The inheritance array for a property
 * @param cmsItems - The CMS model items to resolve references against
 * @returns The resolved value
 */
const getResolvedInheritanceValue = (inheritance: BBInheritanceValue[] | undefined, cmsItems: CmsModel['items']) => {
	const rawValue = last(inheritance)?.value;
	return cms.resolveCmsReferenceValue(rawValue, cmsItems);
};

export {
	getPropConfig,
	getEditableConfig,
	getPropertiesOnMediaChange,
	getParentPath,
	getDefaultValue,
	getPropertyFromName,
	getResolvedInheritanceValue,
	getInheritanceInfo,
};
